import { getCurrentLanguage } from '@src/shared/lib/culture';
import type { Nullable } from '@src/shared/types/common';
import type { IBackPlacesServiceInformation } from '@legacy/scripts/types/modules/back-places-service';

import { applyOnKendoFiltering } from './apply-on-kendo-filtering';

export class BackPlacesService {
    static isInitialization = false;
    static sessionToken: google.maps.places.AutocompleteSessionToken;
    static geocoder: google.maps.Geocoder;
    static statusOK: google.maps.GeocoderStatus.OK;

    static init() {
        if (!this.isInitialization) {
            this.isInitialization = true;
            this.geocoder = new google.maps.Geocoder();
            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
            this.statusOK = google.maps.GeocoderStatus.OK;
        }
    }

    /** Язык запросов к Google: русский — только для русской локали, иначе английский */
    private static getLanguage(): 'ru' | 'en' {
        return getCurrentLanguage() === 'ru' ? 'ru' : 'en';
    }

    static checkFetch(
        status: google.maps.GeocoderStatusString,
        response: () => void,
        reject: Nullable<() => void> = null
    ) {
        if (status === this.statusOK) {
            response();
        }
        else if (reject) {
            reject();
        }
    }

    static async onKendoFiltering(e: kendo.ui.ComboBoxFilteringEvent) {
        BackPlacesService.init();
        await applyOnKendoFiltering(e, (input) => BackPlacesService.getQueryPredictions(input));
    }

    static async getQueryPredictions(input: string): Promise<Array<{ text: string; value: string }>> {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: this.sessionToken,
            language: BackPlacesService.getLanguage()
        });

        return suggestions.map((s) => s.placePrediction)
            .filter((p) => p !== null)
            .map((p) => ({ text: p.text.text, value: p.placeId }));
    }

    static getObjInformation(arr: Nullable<google.maps.GeocoderResult[]>): Nullable<IBackPlacesServiceInformation> {
        if (!arr?.length) {
            return null;
        }
        const { address_components, formatted_address: fullAddress, geometry } = arr[0];
        const { lat, lng } = geometry.location;
        let city = '', region = '', country = '';

        address_components.forEach(({ types, long_name }) => {
            if (types.includes('locality')) {
                city = long_name;
            }
            else if (types.includes('administrative_area_level_1')) {
                region = long_name;
            }
            else if (types.includes('country')) {
                country = long_name;
            }
        });

        return {
            fullAddress,
            city,
            region,
            country,
            coords: [lat(), lng()]
        };
    }

    static getObjectByCoordinates(
        [lat, lng]: number[],
        callback: (info: Nullable<IBackPlacesServiceInformation>) => void,
        language: 'ru' | 'en' = BackPlacesService.getLanguage()
    ) {
        BackPlacesService.init();

        this.geocoder.geocode(
            {
                location: new google.maps.LatLng(lat, lng),
                region: 'ru',
                language
            },
            (results, status) => {
                this.checkFetch(status, () => {
                    callback(this.getObjInformation(results));
                });
            }
        );
    }

    static getObjectByAddress(address: string, callback: (info: Nullable<IBackPlacesServiceInformation>) => void) {
        BackPlacesService.init();

        this.geocoder.geocode(
            {
                address,
                region: 'ru',
                language: BackPlacesService.getLanguage()
            },
            (results, status) => {
                this.checkFetch(status, () => {
                    callback(this.getObjInformation(results));
                });
            }
        );
    }

    static getObjectByPlaceId(placeId: string, callback: (info: Nullable<IBackPlacesServiceInformation>) => void): void {
        BackPlacesService.init();

        this.geocoder.geocode(
            {
                placeId,
                region: 'ru',
                language: BackPlacesService.getLanguage()
            },
            (results, status) => {
                this.checkFetch(status, () => {
                    callback(this.getObjInformation(results));
                });
            }
        );
    }
}

window.BackPlacesService = BackPlacesService;
